import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabase";

type ReturnStatus = "pending" | "approved" | "rejected" | "completed";
type RefundMethod = "cash" | "transfer" | "store_credit" | null;

interface ReturnRow {
  id: number;
  order_id: string;
  customer_name: string;
  customer_phone: string | null;
  product_name: string;
  quantity: number;
  reason: string;
  status: ReturnStatus;
  admin_notes: string | null;
  refund_amount: number | null;
  refund_method: RefundMethod;
  created_at: string;
}

interface MonthOption {
  label: string;
  year: number;
  month: number;
}

function getMonthOptions(): MonthOption[] {
  const opts: MonthOption[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({
      label: d.toLocaleDateString("ar-SA", { year: "numeric", month: "long" }),
      year: d.getFullYear(),
      month: d.getMonth(),
    });
  }
  return opts;
}

function formatCurrency(n: number) {
  return n.toLocaleString("ar-SA", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ر.س";
}

function growthPct(current: number, prev: number): number {
  if (prev === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - prev) / prev) * 100);
}

function GrowthBadge({ current, prev }: { current: number; prev: number }) {
  const pct = growthPct(current, prev);
  if (prev === 0 && current === 0) return null;
  const isUp = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-black px-1.5 py-0.5 rounded-full ${
      isUp ? "bg-red-50 text-red-600" : "bg-green-50 text-green-700"
    }`}>
      <i className={`text-[10px] ${isUp ? "ri-arrow-up-line" : "ri-arrow-down-line"}`}></i>
      {Math.abs(pct)}%
    </span>
  );
}

const STATUS_CONFIG: Record<ReturnStatus, { label: string; color: string; bg: string; border: string; dot: string }> = {
  pending:   { label: "قيد المراجعة", color: "text-amber-700",  bg: "bg-amber-50",   border: "border-amber-200",  dot: "bg-amber-400" },
  approved:  { label: "مقبول",        color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500" },
  rejected:  { label: "مرفوض",        color: "text-red-700",     bg: "bg-red-50",     border: "border-red-200",    dot: "bg-red-500" },
  completed: { label: "مكتمل",        color: "text-sky-700",     bg: "bg-sky-50",     border: "border-sky-200",    dot: "bg-sky-500" },
};

const REFUND_LABELS: Record<string, string> = {
  cash: "نقداً",
  transfer: "تحويل بنكي",
  store_credit: "رصيد المتجر",
};

async function fetchMonthReturns(year: number, month: number): Promise<ReturnRow[]> {
  const start = new Date(year, month, 1).toISOString();
  const end = new Date(year, month + 1, 1).toISOString();
  const { data } = await supabase
    .from("returns")
    .select("*")
    .gte("created_at", start)
    .lt("created_at", end)
    .order("created_at", { ascending: false });
  return data ?? [];
}

async function fetchAllReturns(): Promise<ReturnRow[]> {
  const { data } = await supabase
    .from("returns")
    .select("*")
    .order("created_at", { ascending: false });
  return data ?? [];
}

async function fetchMonthOrdersCount(year: number, month: number): Promise<number> {
  const start = new Date(year, month, 1).toISOString();
  const end = new Date(year, month + 1, 1).toISOString();
  const { count } = await supabase
    .from("orders")
    .select("*", { count: "exact", head: true })
    .gte("created_at", start)
    .lt("created_at", end);
  return count ?? 0;
}

function computeStats(rows: ReturnRow[]) {
  const total = rows.length;
  const pending = rows.filter(r => r.status === "pending").length;
  const approved = rows.filter(r => r.status === "approved").length;
  const rejected = rows.filter(r => r.status === "rejected").length;
  const completed = rows.filter(r => r.status === "completed").length;
  const totalRefund = rows
    .filter(r => r.status === "approved" || r.status === "completed")
    .reduce((s, r) => s + (r.refund_amount ?? 0), 0);
  const approvalRate = total > 0 ? Math.round(((approved + completed) / total) * 100) : 0;
  return { total, pending, approved, rejected, completed, totalRefund, approvalRate };
}

interface Props {
  onToast: (msg: string, type?: "success" | "error") => void;
}

export default function ReturnsReport({ onToast }: Props) {
  const monthOptions = getMonthOptions();

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIdx, setCompareIdx] = useState(1);

  const [returns, setReturns] = useState<ReturnRow[]>([]);
  const [compareReturns, setCompareReturns] = useState<ReturnRow[]>([]);
  const [allReturns, setAllReturns] = useState<ReturnRow[]>([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [compareOrdersCount, setCompareOrdersCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const selected = monthOptions[selectedIdx];
  const compareSelected = monthOptions[compareIdx];

  const loadData = useCallback(async () => {
    setLoading(true);
    const [main, comp, all, oc, coc] = await Promise.all([
      fetchMonthReturns(selected.year, selected.month),
      compareMode ? fetchMonthReturns(compareSelected.year, compareSelected.month) : Promise.resolve([]),
      fetchAllReturns(),
      fetchMonthOrdersCount(selected.year, selected.month),
      compareMode ? fetchMonthOrdersCount(compareSelected.year, compareSelected.month) : Promise.resolve(0),
    ]);
    setReturns(main);
    setCompareReturns(comp);
    setAllReturns(all);
    setOrdersCount(oc);
    setCompareOrdersCount(coc);
    setLoading(false);
  }, [selected, compareSelected, compareMode]);

  useEffect(() => { loadData(); }, [loadData]);

  const stats = useMemo(() => computeStats(returns), [returns]);
  const cStats = useMemo(() => computeStats(compareReturns), [compareReturns]);

  // Return rate = returns / orders * 100
  const returnRate = ordersCount > 0 ? Math.round((stats.total / ordersCount) * 100) : 0;
  const cReturnRate = compareOrdersCount > 0 ? Math.round((cStats.total / compareOrdersCount) * 100) : 0;

  // Daily breakdown
  const daysInMonth = new Date(selected.year, selected.month + 1, 0).getDate();
  const dailyData = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    const dayRows = returns.filter(r => new Date(r.created_at).getDate() === day);
    const cDayRows = compareMode
      ? compareReturns.filter(r => new Date(r.created_at).getDate() === day)
      : [];
    return {
      day,
      count: dayRows.length,
      refund: dayRows.reduce((s, r) => s + (r.refund_amount ?? 0), 0),
      cCount: cDayRows.length,
      cRefund: cDayRows.reduce((s, r) => s + (r.refund_amount ?? 0), 0),
    };
  }), [returns, compareReturns, daysInMonth, compareMode]);

  const maxDayCount = Math.max(...dailyData.map(d => Math.max(d.count, d.cCount)), 1);

  // Reason breakdown
  const reasonMap = useMemo(() => {
    const map: Record<string, number> = {};
    returns.forEach(r => {
      const key = r.reason.length > 30 ? r.reason.slice(0, 30) + "…" : r.reason;
      map[key] = (map[key] ?? 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [returns]);

  // Product breakdown
  const productMap = useMemo(() => {
    const map: Record<string, { count: number; refund: number }> = {};
    returns.forEach(r => {
      if (!map[r.product_name]) map[r.product_name] = { count: 0, refund: 0 };
      map[r.product_name].count += r.quantity;
      map[r.product_name].refund += r.refund_amount ?? 0;
    });
    return Object.entries(map).sort((a, b) => b[1].count - a[1].count).slice(0, 5);
  }, [returns]);

  // Refund method breakdown
  const refundMethodMap = useMemo(() => {
    const map: Record<string, { count: number; total: number }> = {};
    returns
      .filter(r => r.refund_method)
      .forEach(r => {
        const key = r.refund_method!;
        if (!map[key]) map[key] = { count: 0, total: 0 };
        map[key].count++;
        map[key].total += r.refund_amount ?? 0;
      });
    return Object.entries(map);
  }, [returns]);

  // Monthly trend (last 6 months from allReturns)
  const monthlyTrend = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const y = d.getFullYear();
      const m = d.getMonth();
      const count = allReturns.filter(r => {
        const rd = new Date(r.created_at);
        return rd.getFullYear() === y && rd.getMonth() === m;
      }).length;
      return {
        label: d.toLocaleDateString("ar-SA", { month: "short" }),
        count,
      };
    });
  }, [allReturns]);
  const maxTrend = Math.max(...monthlyTrend.map(m => m.count), 1);

  // CSV Export
  const exportCSV = useCallback(() => {
    setExporting(true);
    try {
      const headers = ["#", "رقم الطلب", "العميل", "الجوال", "المنتج", "الكمية", "سبب الإرجاع", "الحالة", "مبلغ الاسترداد", "طريقة الاسترداد", "ملاحظات الأدمن", "التاريخ"];
      const rows = returns.map((r, i) => [
        i + 1,
        r.order_id,
        r.customer_name,
        r.customer_phone ?? "—",
        r.product_name,
        r.quantity,
        r.reason,
        STATUS_CONFIG[r.status]?.label ?? r.status,
        r.refund_amount ? `${r.refund_amount} ر.س` : "—",
        r.refund_method ? REFUND_LABELS[r.refund_method] ?? r.refund_method : "—",
        r.admin_notes ?? "—",
        new Date(r.created_at).toLocaleDateString("ar-SA"),
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));

      const bom = "\uFEFF";
      const csv = bom + [headers.map(h => `"${h}"`).join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `تقرير_المرتجعات_${selected.label}.csv`.replace(/\s/g, "_");
      a.click();
      URL.revokeObjectURL(url);
      onToast("تم تصدير تقرير المرتجعات بنجاح ✓");
    } catch {
      onToast("حدث خطأ أثناء التصدير", "error");
    }
    setExporting(false);
  }, [returns, selected, onToast]);

  return (
    <div className="flex flex-col gap-5">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-black text-[#1a1a1a] text-lg flex items-center gap-2">
            <i className="ri-arrow-go-back-line text-rose-600"></i>
            تقرير المرتجعات الشهري
          </h2>
          <p className="text-gray-400 text-xs mt-0.5">تحليل شامل للمرتجعات ومعدلات الإرجاع</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold text-sm px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-printer-line text-base"></i>
            طباعة
          </button>
          <button
            onClick={exportCSV}
            disabled={exporting || returns.length === 0}
            className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#333] disabled:opacity-50 text-white font-bold text-sm px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            {exporting
              ? <i className="ri-loader-4-line animate-spin text-base"></i>
              : <i className="ri-download-2-line text-base"></i>
            }
            تصدير CSV
          </button>
        </div>
      </div>

      {/* ── Month Selectors ── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 flex-1">
            <div className="w-3 h-3 rounded-full bg-[#1a1a1a] flex-shrink-0"></div>
            <label className="text-xs font-bold text-gray-500 whitespace-nowrap">الشهر الرئيسي</label>
            <select
              value={selectedIdx}
              onChange={e => setSelectedIdx(Number(e.target.value))}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-[#1a1a1a] outline-none focus:border-gray-400 cursor-pointer bg-white"
            >
              {monthOptions.map((m, i) => (
                <option key={i} value={i}>{m.label}</option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setCompareMode(!compareMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap border ${
              compareMode
                ? "bg-rose-500 border-rose-500 text-white"
                : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
            }`}
          >
            <i className="ri-arrow-left-right-line text-base"></i>
            {compareMode ? "إلغاء المقارنة" : "مقارنة بشهر آخر"}
          </button>

          {compareMode && (
            <div className="flex items-center gap-2 flex-1">
              <div className="w-3 h-3 rounded-full bg-rose-400 flex-shrink-0"></div>
              <label className="text-xs font-bold text-gray-500 whitespace-nowrap">شهر المقارنة</label>
              <select
                value={compareIdx}
                onChange={e => setCompareIdx(Number(e.target.value))}
                className="flex-1 border border-rose-300 rounded-lg px-3 py-2 text-sm font-bold text-[#1a1a1a] outline-none focus:border-rose-400 cursor-pointer bg-rose-50"
              >
                {monthOptions.map((m, i) => (
                  <option key={i} value={i} disabled={i === selectedIdx}>{m.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
          <i className="ri-loader-4-line text-3xl animate-spin"></i>
          <p className="text-sm">جاري تحميل التقرير...</p>
        </div>
      ) : (
        <>
          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Total Returns */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-rose-50 rounded-lg">
                  <i className="ri-arrow-go-back-line text-rose-600 text-sm"></i>
                </div>
                {compareMode && <GrowthBadge current={stats.total} prev={cStats.total} />}
              </div>
              <p className="text-2xl font-black text-[#1a1a1a]">{stats.total}</p>
              <p className="text-xs text-gray-400 mt-0.5">إجمالي المرتجعات</p>
              {compareMode && (
                <p className="text-[10px] text-rose-500 mt-1 font-bold">
                  {compareSelected.label}: <span className="text-gray-500">{cStats.total}</span>
                </p>
              )}
            </div>

            {/* Return Rate */}
            <div className="bg-[#1a1a1a] rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
                  <i className="ri-percent-line text-white text-sm"></i>
                </div>
                {compareMode && (
                  <span className={`inline-flex items-center gap-0.5 text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                    growthPct(returnRate, cReturnRate) <= 0
                      ? "bg-green-400/20 text-green-400"
                      : "bg-red-400/20 text-red-400"
                  }`}>
                    <i className={`text-[10px] ${growthPct(returnRate, cReturnRate) <= 0 ? "ri-arrow-down-line" : "ri-arrow-up-line"}`}></i>
                    {Math.abs(growthPct(returnRate, cReturnRate))}%
                  </span>
                )}
              </div>
              <p className="text-2xl font-black text-white">{returnRate}%</p>
              <p className="text-xs text-white/50 mt-0.5">معدل الإرجاع</p>
              <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(returnRate, 100)}%`,
                    background: returnRate > 20 ? "#ef4444" : returnRate > 10 ? "#f59e0b" : "#10b981",
                  }}
                ></div>
              </div>
              {compareMode && (
                <p className="text-[10px] text-rose-400 mt-1 font-bold">
                  {compareSelected.label}: <span className="text-white/50">{cReturnRate}%</span>
                </p>
              )}
            </div>

            {/* Total Refund */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg">
                  <i className="ri-refund-2-line text-emerald-600 text-sm"></i>
                </div>
                {compareMode && <GrowthBadge current={stats.totalRefund} prev={cStats.totalRefund} />}
              </div>
              <p className="text-lg font-black text-[#1a1a1a] leading-tight">{formatCurrency(stats.totalRefund)}</p>
              <p className="text-xs text-gray-400 mt-0.5">إجمالي المسترد</p>
              {compareMode && (
                <p className="text-[10px] text-rose-500 mt-1 font-bold">
                  {compareSelected.label}: <span className="text-gray-500">{formatCurrency(cStats.totalRefund)}</span>
                </p>
              )}
            </div>

            {/* Approval Rate */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-sky-50 rounded-lg">
                  <i className="ri-checkbox-circle-line text-sky-600 text-sm"></i>
                </div>
                {compareMode && (
                  <span className={`inline-flex items-center gap-0.5 text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                    growthPct(stats.approvalRate, cStats.approvalRate) >= 0
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-600"
                  }`}>
                    <i className={`text-[10px] ${growthPct(stats.approvalRate, cStats.approvalRate) >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"}`}></i>
                    {Math.abs(growthPct(stats.approvalRate, cStats.approvalRate))}%
                  </span>
                )}
              </div>
              <p className="text-2xl font-black text-[#1a1a1a]">{stats.approvalRate}%</p>
              <p className="text-xs text-gray-400 mt-0.5">نسبة القبول</p>
              <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-sky-400 rounded-full transition-all" style={{ width: `${stats.approvalRate}%` }}></div>
              </div>
              {compareMode && (
                <p className="text-[10px] text-rose-500 mt-1 font-bold">
                  {compareSelected.label}: <span className="text-gray-500">{cStats.approvalRate}%</span>
                </p>
              )}
            </div>
          </div>

          {/* ── Compare Banner ── */}
          {compareMode && (
            <div className="bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] rounded-xl p-5">
              <p className="text-white font-black text-sm mb-4 flex items-center gap-2">
                <i className="ri-arrow-left-right-line text-rose-400"></i>
                ملخص المقارنة: {selected.label} مقابل {compareSelected.label}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "المرتجعات", main: stats.total, comp: cStats.total, fmt: (v: number) => `${v}`, lowerIsBetter: true },
                  { label: "معدل الإرجاع", main: returnRate, comp: cReturnRate, fmt: (v: number) => `${v}%`, lowerIsBetter: true },
                  { label: "إجمالي المسترد", main: stats.totalRefund, comp: cStats.totalRefund, fmt: formatCurrency, lowerIsBetter: true },
                  { label: "نسبة القبول", main: stats.approvalRate, comp: cStats.approvalRate, fmt: (v: number) => `${v}%`, lowerIsBetter: false },
                ].map(item => {
                  const pct = growthPct(item.main, item.comp);
                  const isGood = item.lowerIsBetter ? pct <= 0 : pct >= 0;
                  return (
                    <div key={item.label} className="bg-white/5 rounded-xl p-3">
                      <p className="text-white/50 text-[10px] mb-2">{item.label}</p>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 rounded-full bg-white flex-shrink-0"></div>
                        <p className="text-white text-xs font-black">{item.fmt(item.main)}</p>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-rose-400 flex-shrink-0"></div>
                        <p className="text-rose-400 text-xs font-bold">{item.fmt(item.comp)}</p>
                      </div>
                      <div className={`flex items-center gap-1 text-[11px] font-black ${isGood ? "text-green-400" : "text-red-400"}`}>
                        <i className={`text-xs ${pct >= 0 ? "ri-arrow-up-circle-fill" : "ri-arrow-down-circle-fill"}`}></i>
                        {pct >= 0 ? "+" : ""}{pct}%
                        <span className="text-white/30 font-normal text-[10px]">{isGood ? "تحسن" : "تراجع"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Monthly Trend (last 6 months) ── */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-bold text-[#1a1a1a]">الاتجاه الشهري</p>
                <p className="text-xs text-gray-400 mt-0.5">عدد المرتجعات آخر 6 أشهر</p>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <i className="ri-information-line text-sm"></i>
                <span>الأعلى = {Math.max(...monthlyTrend.map(m => m.count))} مرتجع</span>
              </div>
            </div>
            <div className="flex items-end gap-2 h-24">
              {monthlyTrend.map((m, i) => {
                const h = maxTrend === 0 ? 0 : (m.count / maxTrend) * 100;
                const isCurrentMonth = i === monthlyTrend.length - 1;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1.5 group relative">
                    <div
                      className={`w-full rounded-t-lg transition-all duration-500 ${
                        isCurrentMonth ? "bg-rose-500" : m.count > 0 ? "bg-[#1a1a1a] group-hover:bg-[#333]" : "bg-gray-100"
                      }`}
                      style={{ height: `${Math.max(h, m.count > 0 ? 8 : 4)}%` }}
                    ></div>
                    {m.count > 0 && (
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-[#1a1a1a] text-white text-[9px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        {m.count} مرتجع
                      </div>
                    )}
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{m.label}</span>
                    {m.count > 0 && (
                      <span className={`text-[10px] font-black ${isCurrentMonth ? "text-rose-500" : "text-gray-600"}`}>
                        {m.count}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Daily Chart ── */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-bold text-[#1a1a1a]">المرتجعات اليومية</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {selected.label}{compareMode ? ` مقابل ${compareSelected.label}` : ""}
                </p>
              </div>
              <span className="text-xs font-black text-[#1a1a1a]">
                {stats.total} مرتجع
              </span>
            </div>
            <div className="flex items-end gap-0.5 h-20">
              {dailyData.map((d) => {
                const h = maxDayCount === 0 ? 0 : (d.count / maxDayCount) * 100;
                const ch = maxDayCount === 0 ? 0 : (d.cCount / maxDayCount) * 100;
                const isToday =
                  new Date().getDate() === d.day &&
                  new Date().getMonth() === selected.month &&
                  new Date().getFullYear() === selected.year;
                return (
                  <div key={d.day} className="flex-1 flex items-end gap-px group relative" style={{ height: "100%" }}>
                    <div
                      className={`flex-1 rounded-t-sm transition-all ${
                        isToday ? "bg-rose-500" : d.count > 0 ? "bg-[#1a1a1a] group-hover:bg-[#333]" : "bg-gray-100"
                      }`}
                      style={{ height: `${Math.max(h, d.count > 0 ? 8 : 2)}%` }}
                    ></div>
                    {compareMode && (
                      <div
                        className={`flex-1 rounded-t-sm transition-all ${d.cCount > 0 ? "bg-rose-300 group-hover:bg-rose-400" : "bg-gray-50"}`}
                        style={{ height: `${Math.max(ch, d.cCount > 0 ? 8 : 2)}%` }}
                      ></div>
                    )}
                    {(d.count > 0 || d.cCount > 0) && (
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-[#1a1a1a] text-white text-[9px] px-2 py-1.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center">
                        <p className="font-bold">{d.day} {selected.label.split(" ")[0]}</p>
                        {d.count > 0 && <p className="text-gray-300">{d.count} مرتجع</p>}
                        {compareMode && d.cCount > 0 && <p className="text-rose-300">{d.cCount} مرتجع</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-[10px] text-gray-400">1</span>
              <span className="text-[10px] text-gray-400">{Math.round(daysInMonth / 2)}</span>
              <span className="text-[10px] text-gray-400">{daysInMonth}</span>
            </div>
          </div>

          {/* ── 3-col: Status + Reasons + Refund Methods ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Status Distribution */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-sm font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
                <i className="ri-pie-chart-line text-gray-400 text-sm"></i>
                توزيع الحالات
              </p>
              {stats.total === 0 ? (
                <p className="text-xs text-gray-300 text-center py-4">لا توجد مرتجعات</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {(Object.keys(STATUS_CONFIG) as ReturnStatus[]).map(s => {
                    const cfg = STATUS_CONFIG[s];
                    const count = stats[s as keyof typeof stats] as number;
                    const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                    const cCount = cStats[s as keyof typeof cStats] as number;
                    if (count === 0 && cCount === 0) return null;
                    return (
                      <div key={s}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`}></span>
                            <span className={`text-xs font-bold ${cfg.color}`}>{cfg.label}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-black text-[#1a1a1a]">{count}</span>
                            {compareMode && cCount > 0 && (
                              <span className="text-[10px] text-rose-500">/ {cCount}</span>
                            )}
                          </div>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full ${cfg.dot} rounded-full`} style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Top Reasons */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-sm font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
                <i className="ri-question-answer-line text-gray-400 text-sm"></i>
                أبرز أسباب الإرجاع
              </p>
              {reasonMap.length === 0 ? (
                <p className="text-xs text-gray-300 text-center py-4">لا توجد بيانات</p>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {reasonMap.map(([reason, count], i) => {
                    const maxCount = reasonMap[0][1];
                    const pct = Math.round((count / maxCount) * 100);
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[10px] font-black w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 ${
                              i === 0 ? "bg-rose-500 text-white" : "bg-gray-100 text-gray-500"
                            }`}>{i + 1}</span>
                            <span className="text-xs text-[#1a1a1a] truncate max-w-[120px]">{reason}</span>
                          </div>
                          <span className="text-xs font-black text-[#1a1a1a] flex-shrink-0">{count}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-rose-400 rounded-full" style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Refund Methods */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-sm font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
                <i className="ri-bank-card-line text-gray-400 text-sm"></i>
                طرق الاسترداد
              </p>
              {refundMethodMap.length === 0 ? (
                <p className="text-xs text-gray-300 text-center py-4">لا توجد بيانات</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {refundMethodMap.map(([method, data]) => (
                    <div key={method} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 flex items-center justify-center bg-gray-50 rounded-lg flex-shrink-0">
                          <i className={`text-xs ${
                            method === "transfer" ? "ri-bank-line text-amber-600"
                            : method === "store_credit" ? "ri-gift-line text-emerald-600"
                            : "ri-money-dollar-circle-line text-green-600"
                          }`}></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-[#1a1a1a]">{REFUND_LABELS[method] ?? method}</p>
                          <p className="text-[10px] text-gray-400">{data.count} مرتجع</p>
                        </div>
                      </div>
                      <p className="text-xs font-black text-[#1a1a1a]">{formatCurrency(data.total)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Top Returned Products ── */}
          {productMap.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <p className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
                <i className="ri-arrow-go-back-line text-rose-500 text-sm"></i>
                أكثر المنتجات إرجاعاً
              </p>
              <div className="flex flex-col gap-3">
                {productMap.map(([name, data], i) => {
                  const maxCount = productMap[0][1].count;
                  const pct = Math.round((data.count / maxCount) * 100);
                  return (
                    <div key={name} className="flex items-center gap-3">
                      <span className={`text-xs font-black w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${
                        i === 0 ? "bg-rose-500 text-white"
                        : i === 1 ? "bg-gray-300 text-gray-700"
                        : i === 2 ? "bg-amber-200 text-amber-800"
                        : "bg-gray-100 text-gray-500"
                      }`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-bold text-[#1a1a1a] truncate">{name}</p>
                          <div className="flex items-center gap-2 flex-shrink-0 mr-2">
                            <span className="text-[10px] text-gray-500">{data.count} قطعة</span>
                            {data.refund > 0 && (
                              <span className="text-[10px] font-black text-emerald-600">{formatCurrency(data.refund)}</span>
                            )}
                          </div>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-rose-400 rounded-full" style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Returns Table ── */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <p className="text-sm font-bold text-[#1a1a1a] flex items-center gap-2">
                <i className="ri-file-list-3-line text-gray-400 text-sm"></i>
                تفاصيل المرتجعات
                <span className="text-xs text-gray-400 font-normal">({returns.length} مرتجع)</span>
              </p>
              <button
                onClick={exportCSV}
                disabled={exporting || returns.length === 0}
                className="flex items-center gap-1.5 text-xs font-bold text-[#1a1a1a] hover:text-rose-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                <i className="ri-download-2-line text-sm"></i>
                تصدير
              </button>
            </div>

            {returns.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-300">
                <i className="ri-arrow-go-back-line text-4xl mb-3"></i>
                <p className="text-sm">لا توجد مرتجعات في {selected.label}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {["رقم الطلب", "العميل", "المنتج", "الكمية", "الحالة", "المسترد", "التاريخ"].map(h => (
                        <th key={h} className="text-right text-[11px] font-bold text-gray-400 px-4 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {returns.map((r, idx) => {
                      const cfg = STATUS_CONFIG[r.status];
                      const date = new Date(r.created_at).toLocaleDateString("ar-SA", { month: "short", day: "numeric" });
                      return (
                        <tr key={r.id} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${idx % 2 === 0 ? "" : "bg-gray-50/30"}`}>
                          <td className="px-4 py-3">
                            <span className="font-bold text-[#1a1a1a] text-xs bg-gray-100 px-2 py-1 rounded-lg">
                              #{r.order_id}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs font-bold text-[#1a1a1a] whitespace-nowrap">{r.customer_name}</p>
                            {r.customer_phone && <p className="text-[10px] text-gray-400">{r.customer_phone}</p>}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-gray-700 max-w-[140px] truncate">{r.product_name}</p>
                          </td>
                          <td className="px-4 py-3 text-xs font-bold text-[#1a1a1a]">{r.quantity}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border} whitespace-nowrap`}>
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`}></span>
                              {cfg.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {r.refund_amount ? (
                              <span className="text-xs font-black text-emerald-600 whitespace-nowrap">
                                {r.refund_amount.toLocaleString("ar-SA")} ر.س
                              </span>
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{date}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#1a1a1a]">
                      <td colSpan={5} className="px-4 py-3 text-white text-xs font-bold">
                        الإجمالي — {returns.length} مرتجع
                      </td>
                      <td className="px-4 py-3 text-emerald-400 font-black text-sm whitespace-nowrap">
                        {formatCurrency(stats.totalRefund)}
                      </td>
                      <td className="px-4 py-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
